import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import api from '../services/api';
import {
  CheckCircle2,
  Loader2,
  ArrowRight,
  XCircle,
  AlertCircle,
  CreditCard,
  Home,
  Receipt,
  Star
} from 'lucide-react';

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isAuthenticated } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [sessionInfo, setSessionInfo] = useState(null);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const freeAccess = searchParams.get('free');
    
    if (freeAccess === 'true') {
      setSuccess(true);
      setSessionInfo({
        product_name: 'Acceso Gratuito',
        amount_total: 0,
        currency: 'MXN'
      });
      setLoading(false);
      return;
    }
    
    if (!sessionId) {
      setError('No se encontró información del pago');
      setLoading(false);
      return;
    }

    confirmPayment(sessionId);
  }, [searchParams]);

  const confirmPayment = async (sessionId) => {
    try {
      const response = await api.get(`/checkout/session/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setSuccess(true);
        setSessionInfo(response.data);
        toast({
          title: "¡Pago exitoso!",
          description: "Tu compra se ha procesado correctamente"
        });
      } else {
        setError(`Estado del pago: ${response.data.payment_status}. Por favor contacta a soporte.`);
      }
    } catch (error) {
      console.error('Error confirming payment:', error);
      setError(error.response?.data?.detail || 'Error al confirmar el pago');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <Loader2 className="h-12 w-12 animate-spin text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Confirmando tu pago...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
          <Card className="max-w-md w-full">
            <CardContent className="p-8 text-center">
              <div className="h-16 w-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
                <XCircle className="h-8 w-8 text-red-600" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-4">
                Error en el pago
              </h1>
              <p className="text-gray-600 mb-6">{error}</p>
              <div className="flex flex-col gap-3">
                <Button 
                  onClick={() => navigate('/afiliacion')}
                  className="bg-gray-900 hover:bg-gray-800"
                >
                  Intentar de nuevo
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => navigate('/contacto')}
                >
                  Contactar soporte
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-gray-50 py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <Card className="shadow-xl border-0 overflow-hidden">
            {/* Success Header */}
            <div className="bg-gradient-to-r from-green-600 to-green-500 p-8 text-center text-white">
              <div className="h-20 w-20 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="h-10 w-10" />
              </div>
              <h1 className="text-3xl font-bold mb-2">¡Pago Exitoso!</h1>
              <p className="text-green-100">Tu compra se ha procesado correctamente</p>
            </div>
            
            <CardContent className="p-8">
              {/* Purchase Info */}
              {sessionInfo && (
                <div className="bg-gray-50 rounded-xl p-6 mb-6">
                  <div className="flex items-center mb-4">
                    <Receipt className="h-5 w-5 text-gray-500 mr-2" />
                    <span className="font-medium text-gray-700">Detalles de tu compra</span>
                  </div>
                  
                  <div className="space-y-3">
                    {sessionInfo.metadata?.product_name && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Producto</span>
                        <span className="font-medium text-gray-900">{sessionInfo.metadata.product_name}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total pagado</span>
                      <span className="font-bold text-gray-900">
                        ${sessionInfo.amount_total?.toLocaleString()} {sessionInfo.currency?.toUpperCase()}
                      </span>
                    </div>
                    {sessionInfo.customer_email && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Email</span>
                        <span className="text-gray-900">{sessionInfo.customer_email}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Membership Benefits Notice */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
                <div className="flex items-start">
                  <Star className="h-5 w-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-blue-800">
                    <p className="font-semibold mb-1">¡Bienvenido a UPIM!</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Tu membresía ya está activa</li>
                      <li>Accede a tu dashboard para ver todos tus beneficios</li>
                      <li>Se ha enviado un recibo a tu correo electrónico</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  className="flex-1 bg-gray-900 hover:bg-gray-800 h-12"
                  onClick={() => navigate('/dashboard')}
                  data-testid="go-to-dashboard"
                >
                  Ir a mi Dashboard
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button 
                  variant="outline"
                  className="flex-1 h-12"
                  onClick={() => navigate('/')}
                >
                  <Home className="mr-2 h-5 w-5" />
                  Volver al inicio
                </Button>
              </div>

              {/* Footer */}
              <p className="text-center text-sm text-gray-500 mt-6">
                ¿Tienes alguna duda?{' '}
                <Link to="/contacto" className="text-gray-900 underline hover:no-underline">
                  Contáctanos
                </Link>
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default PaymentSuccessPage;
